import { useState } from 'react';

import type { RescheduleAvailableHour } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/variants/_components/appointment-options/_services/get-appointment-avaiable-hours.service';
import { useToast } from '@/components/ui/use-toast';
import { rescheduleAppointment } from '@/services/appointment/reschedule-appointment.service';
import { dateFormatter } from '@/utils/formatters/date';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface UseRescheduleAppointmentParams {
  appointmentId: string;
  onSuccess: (slot: RescheduleAvailableHour) => void;
}

interface UseRescheduleAppointmentResult {
  selectSlot: (slot: RescheduleAvailableHour) => Promise<void>;
  isReschedulingSlotId: string | null;
}

interface BackendError {
  response?: { data?: { message?: string } };
}

const FALLBACK_ERROR = 'Não foi possível reagendar. Tente outro horário.';

const hasBackendMessage = (error: unknown): error is BackendError =>
  typeof error === 'object'
  && error !== null
  && 'response' in error
  && typeof (error as BackendError).response?.data?.message === 'string';

export const useRescheduleAppointment = ({
  appointmentId,
  onSuccess,
}: UseRescheduleAppointmentParams): UseRescheduleAppointmentResult => {
  const { toast } = useToast();
  const analytics = getAnalyticsPlugin();
  const [isReschedulingSlotId, setIsReschedulingSlotId] = useState<string | null>(null);

  const selectSlot = async (slot: RescheduleAvailableHour): Promise<void> => {
    if (isReschedulingSlotId) {
      return;
    }
    setIsReschedulingSlotId(slot.id);
    try {
      await rescheduleAppointment({ id: appointmentId, body: { slotId: slot.id } });
      analytics.track(AnalyticsEnum.MY_APPOINTMENTS_RESCHEDULE_SUCCESS);
      const formatted = dateFormatter(slot.start, 'DD/MM/YYYY [às] HH:mm');
      toast.success(`Atendimento atualizado para ${formatted}`);
      onSuccess(slot);
    } catch (error: unknown) {
      const message = hasBackendMessage(error)
        ? (error.response?.data?.message ?? FALLBACK_ERROR)
        : FALLBACK_ERROR;
      toast.error(message);
    } finally {
      setIsReschedulingSlotId(null);
    }
  };

  return { selectSlot, isReschedulingSlotId };
};
